import { Component, Output, EventEmitter, Input, OnInit, OnDestroy } from "@angular/core";
import { UntypedFormBuilder, Validators } from "@angular/forms";
import { startWith, map } from "rxjs/operators";
import { Observable } from "rxjs";

import { ForgotPasswordModel, LoginModel } from "../../auth-models";
import { UserService } from "src/app/shared/services/user.service";
// import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component';
import { AuthService } from "../../auth.service";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { NgRecaptcha3Service } from 'ng-recaptcha3';
import { environment } from '../../../../environments/environment';

@Component({
  selector: "app-login-form",
  templateUrl: "./login-form.component.html",
  styleUrls: ["./login-form.component.scss"],
  standalone: false,
})
export class LoginFormComponent implements OnInit, OnDestroy {
  @Input() loginFailed: boolean;
  @Input() enterTwoFactorCodeInput: boolean;
  @Input() errorMessage: string;
  @Output() submitted = new EventEmitter<LoginModel>();
  @Output() recaptchaFailed = new EventEmitter<void>();
  options: string[];
  filteredLogins: Observable<string[]>;
  showerror: boolean;
  hidePassword = true;

  /*
  // Moved to ngOnInit to avoid initialization errors.
  loginForm = this.fb.group({
    email: [
      "",
      {
        validators: [Validators.required, Validators.email],
      },
    ],
    password: [null, { validators: [Validators.required] }],
  });
  */
  loginForm: any;

  get email() {
    return this.loginForm.get("email");
  }

  get password() {
    return this.loginForm.get("password");
  }

  get code() {
    return this.loginForm.get("code");
  }

  constructor(
    private fb: UntypedFormBuilder,
    private userService: UserService,
    public dialog: MatDialog,
    private authService: AuthService,
    private router: Router,
    private recaptcha3: NgRecaptcha3Service,
  ) {}

  ngOnInit() {
    this.loginForm = this.fb.group({
      email: [
        "",
        {
          validators: [Validators.required, Validators.email],
        },
      ],
      password: [null, { validators: [Validators.required] }],
      code: [null],
    });

    // A rejected init leaves window.grecaptcha undefined. Swallowing it here is safe
    // because onSubmit retries before giving up, but it must be caught: an unhandled
    // rejection surfaces as a console error on a page that is otherwise fine.
    this.initRecaptcha().catch(() =>
      console.warn("reCAPTCHA failed to load; sign in will retry on submit.")
    );
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.options.filter((option) =>
      option.toLowerCase().includes(filterValue)
    );
  }

  onSubmit() {
    this.loginForm.markAllAsTouched();
    this.loginForm.updateValueAndValidity();

    if (!this.loginForm.valid) {
      return;
    }

    //skip recaptcha if there is a code
    if (this.loginForm.value.code) {
      this.submitted.emit(this.loginForm.value);
      return;
    }

    this.getRecaptchaToken().then(
      (token) => {
        this.loginForm.value.recaptchaToken = token;
        this.submitted.emit(this.loginForm.value);
      },
      (err: unknown) => {
        // The server rejects a login with no token, so there is nothing worth
        // submitting. Report it instead of leaving Submit looking unresponsive.
        console.error("reCAPTCHA unavailable", err);
        this.recaptchaFailed.emit();
      }
    );
  }

  /**
   * getToken() rejects when the reCAPTCHA script never loaded, so retry init() once.
   * Without the retry a single failed script load leaves Submit dead for as long as
   * the page stays open, with no way back other than a manual refresh.
   */
  private getRecaptchaToken(): Promise<string> {
    return this.recaptcha3
      .getToken()
      .catch(() => this.initRecaptcha().then(() => this.recaptcha3.getToken()));
  }

  private initRecaptcha() {
    return this.recaptcha3.init(environment.recaptcha.siteKey);
  }

  getErrorMessage(isPassword = false) {
    if (isPassword) {
      return this.password.hasError("required") ? "Password is required" : "";
    }
    return this.email.hasError("required")
      ? "Email is required"
      : this.email.hasError("email")
      ? "Not a valid email"
      : "";
  }

  forgotPassword(evt) {
    evt.preventDefault();
    this.router.navigate(["/", "auth", "forgot-password"]);
  }

  public ngOnDestroy() {
    this.recaptcha3.destroy();
  }
}
